import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, ChevronRight, Users, CheckCircle, TrendingUp, BarChart2, Calendar } from 'lucide-react';
import { useFeedbackInstances } from '../hooks/useFeedbackInstanceApi';
import { useInstanceAnalytics } from '../hooks/useAnalyticsApi';
import { FeedbackInstance } from '../services/feedbackInstanceService';
import MetricCard from '../components/charts/MetricCard';
import PieChartWidget from '../components/charts/PieChartWidget';
import BarChartWidget from '../components/charts/BarChartWidget';
import { QuestionAnalytics } from '../types/analytics';

type StatusFilter = 'all' | 'active' | 'completed' | 'scheduled';

const statusBadgeClasses: Record<FeedbackInstance['status'], string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  scheduled: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const questionTypeBadgeClasses: Record<string, string> = {
  rating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  radiogroup: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  checkbox: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  dropdown: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  boolean: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  text: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  comment: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

const QuestionChartCard: React.FC<{ question: QuestionAnalytics }> = ({ question }) => {
  const badgeClass = questionTypeBadgeClasses[question.questionType] ?? questionTypeBadgeClasses.text;

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {question.questionText}
        </h4>
        <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
          {question.questionType}
        </span>
      </div>
      {question.chartType === 'pie' ? (
        <PieChartWidget data={question.chartData} height={200} />
      ) : (
        <BarChartWidget data={question.chartData} height={200} />
      )}
    </div>
  );
};

const TextResponseCard: React.FC<{ question: QuestionAnalytics }> = ({ question }) => {
  const responses = question.textResponses ?? [];
  if (responses.length === 0) return null;

  const displayed = responses.slice(0, 3);
  const remaining = responses.length - displayed.length;
  const badgeClass = questionTypeBadgeClasses[question.questionType] ?? questionTypeBadgeClasses.text;

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {question.questionText}
        </h4>
        <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
          {question.questionType}
        </span>
      </div>
      <div className="space-y-2">
        {displayed.map((response, index) => (
          <div
            key={index}
            className="border-l-2 border-gray-300 dark:border-gray-600 pl-3"
          >
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {response.respondentName}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 italic line-clamp-2">
              &ldquo;{response.text}&rdquo;
            </p>
          </div>
        ))}
        {remaining > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            (+{remaining} more)
          </p>
        )}
      </div>
    </div>
  );
};

const InlineAnalyticsSummary: React.FC<{ instanceId: string }> = ({ instanceId }) => {
  const navigate = useNavigate();
  const { data: analytics, isLoading } = useInstanceAnalytics(instanceId);

  if (isLoading) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-center py-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">Analytics not available</p>
      </div>
    );
  }

  const hasResponses = analytics.totalResponses > 0;
  const questionAnalytics = analytics.questionAnalytics ?? [];
  const chartQuestions = questionAnalytics.filter((q) => q.chartType === 'pie' || q.chartType === 'bar');
  const textQuestions = questionAnalytics.filter((q) => q.chartType === 'text');

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard title="Total Responses" value={analytics.totalResponses} icon={Users} color="blue" />
        <MetricCard title="Completed" value={analytics.completedResponses} icon={CheckCircle} color="green" />
        <MetricCard title="Completion Rate" value={`${analytics.completionRate}%`} icon={TrendingUp} color="purple" />
      </div>

      {analytics.totalFlagsTriggered > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <span className="text-red-600 dark:text-red-400 text-sm font-medium">
            🚩 {analytics.totalFlagsTriggered} flag(s) triggered across {analytics.totalFlaggedQuestions} question(s)
          </span>
        </div>
      )}

      {hasResponses && questionAnalytics.length > 0 && (
        <div className="space-y-4">
          {chartQuestions.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {chartQuestions.map((question) => (
                <QuestionChartCard key={question.questionId} question={question} />
              ))}
            </div>
          )}

          {textQuestions.map((question) => (
            <TextResponseCard key={question.questionId} question={question} />
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => navigate(`/analytics/${instanceId}`)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
          <BarChart2 className="h-4 w-4 mr-2" />
          View Full Analytics
        </button>
      </div>
    </div>
  );
};

const Results: React.FC = () => {
  const { data: instances, isLoading } = useFeedbackInstances();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredInstances = useMemo(() => {
    if (!instances) return [];
    const lowerSearch = searchText.toLowerCase();
    return instances
      .filter((instance) => {
        if (statusFilter !== 'all' && instance.status !== statusFilter) return false;
        if (lowerSearch) {
          const matchesTitle = instance.feedbackTitle?.toLowerCase().includes(lowerSearch);
          if (!matchesTitle) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [instances, searchText, statusFilter]);

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse" />
        <div className="flex gap-4">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded flex-1 animate-pulse" />
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Results</h1>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by feedback title or project..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="scheduled">Scheduled</option>
        </select>
      </div>

      {filteredInstances.length === 0 ? (
        <div className="text-center py-16">
          <BarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            {instances?.length === 0 ? 'No feedback instances yet' : 'No instances match your filters'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            {instances?.length === 0
              ? 'Results will appear here once feedback instances are created.'
              : 'Try adjusting your search or status filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredInstances.map((instance) => {
            const isExpanded = expandedId === instance.id;
            return (
              <div
                key={instance.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
              >
                <button
                  onClick={() => toggleExpand(instance.id)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {instance.feedbackTitle || 'Untitled Feedback'}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClasses[instance.status]}`}>
                          {instance.status}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          v{instance.versionNo}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(instance.startDate)}
                          {instance.endDate && ` — ${formatDate(instance.endDate)}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
                {isExpanded && <InlineAnalyticsSummary instanceId={instance.id} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Results;
